import { Component, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { NursingAuditService } from 'src/app/services/nursing-audit.service';
import { MedicationGroup } from '../../models/medication-group';
import { AuthService } from 'src/app/services/auth.service';
import { concatMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-add-items',
  templateUrl: './add-items.component.html',
  styleUrls: ['./add-items.component.scss'],
  standalone: false
})
export class AddItemsComponent implements OnInit {
  private readonly auditService = inject(NursingAuditService);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);

  assayManagementEnabled = environment.enableAssayManagement;
  @Output() back = new EventEmitter<void>();

  breadcrumbItems: MenuItem[] = [];

  searchQuery = '';
  suggestions: MedicationGroup[] = [];
  allItems: MedicationGroup[] = [];
  selectedItem: MedicationGroup | null = null;

  selectedWeight: number | null = null;
  assayTrackable = false;
  showConfirmDialog = false;
  ngOnInit(): void {
    this.breadcrumbItems = [
      { label: 'Monitored Items', command: () => this.back.emit() },
      { label: 'Add Item' }
    ];
    this.auditService.getAvailableMedicationGroups().subscribe((res: any) => {
      this.allItems = res.medicationData || [];
    });
  }

  onSearch(): void {
    const query = (this.searchQuery || '').trim().toLowerCase();
    if (query.length < 1) {
      this.suggestions = [];
      this.selectedItem = null;
      return;
    }
    this.suggestions = this.allItems.filter(m =>
      m.medGroupName.toLowerCase().includes(query)
    );
  }

  selectItem(item: MedicationGroup): void {
    this.selectedItem = item;
    this.searchQuery = '';
    this.suggestions = [];
  }

  clearSelection(): void {
    this.selectedItem = null;
    this.searchQuery = '';
    this.suggestions = [];
  }

  onWeightChange(weight: number): void {
    this.selectedWeight = weight;
  }

  get weightLabel(): string {
    if (this.selectedWeight === 2) return 'Low';
    if (this.selectedWeight === 5) return 'Medium';
    if (this.selectedWeight === 8) return 'High';
    return '';
  }

  get canConfirm(): boolean {
    return !!this.selectedItem && this.selectedWeight !== null;
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.showConfirmDialog = true;
  }

  confirmSave(): void {
    if (!this.selectedItem) {
      return;
    }

    this.showConfirmDialog = false;
    const payload = {
      medGroupName: this.selectedItem.medGroupName,
      diversionWeight: this.selectedWeight,
      assayTrackable: this.assayTrackable
    };
    this.auditService.enableMedicationGroup(payload).pipe(
      concatMap(() => this.auditService.saveItemsMonitorHistory(this.buildItemsMonitorHistoryPayload()))
    ).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Item group added' });
        this.back.emit();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Failed to add item group' });
      }
    });
  }

  private buildItemsMonitorHistoryPayload(): any {
    const currentUser = this.authService.user || {};
    return {
      user: currentUser.fullname || '',
      userId: currentUser._id || currentUser.id || '',
      ChangedAt: new Date(),
      medId: this.getMedId(this.selectedItem),
      medGroupName: this.selectedItem?.medGroupName,
      diversionWeight: this.selectedWeight,
      assayTrackable: this.assayTrackable
    };
  }

  private getMedId(medication: MedicationGroup | null): any {
    const mappedItem = medication?.mappedItems?.find(item =>
      this.hasMedId(item.adcMedId) || this.hasMedId(item.ehrMedId)
    );
    if (mappedItem) {
      return this.hasMedId(mappedItem.adcMedId) ? mappedItem.adcMedId : mappedItem.ehrMedId;
    }

    const adcMedId = medication?.adcMedId?.find(id => this.hasMedId(id));
    if (this.hasMedId(adcMedId)) {
      return adcMedId;
    }

    const ehrMedId = medication?.ehrMedId?.find(id => this.hasMedId(id));
    return this.hasMedId(ehrMedId) ? ehrMedId : '';
  }

  private hasMedId(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  cancelConfirm(): void {
    this.showConfirmDialog = false;
  }

  cancel(): void {
    this.back.emit();
  }

  onBreadcrumbClick(event: any): void {
    if (event.item?.command) {
      event.item.command();
    }
  }
}
